import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { createActorState, type ActorStateOf } from '../../state/actor-state';
import {
  requestSignInCode,
  signInWithPassword,
  verifySignInCode,
} from '../../data/live';
import { setEmbedToken } from '../../config/client';

type SignInMode = 'password' | 'code';

const signInActorState = createActorState(() => ({
  mode: 'password' as SignInMode,
  stage: 'email' as 'email' | 'code',
  email: '',
  password: '',
  code: '',
  busy: false,
  error: '',
  info: '',
}));

type SignInActorState = ActorStateOf<typeof signInActorState>;

/**
 * Customer sign-in modal for embeds. Supports password login and email OTP.
 * Events: `zipper-signed-in` (detail {firstName?}) and `zipper-signin-close`.
 */
@customElement('zipper-signin')
export class ZipperSignin extends LitElement {
  protected createRenderRoot(): HTMLElement {
    return this;
  }

  @property({ type: Number }) orgId = 0;
  @property({ type: String }) accent = '#4F9DB5';

  @signInActorState.field('mode')
  private mode!: SignInActorState['mode'];

  @signInActorState.field('stage')
  private stage!: SignInActorState['stage'];

  @signInActorState.field('email')
  private email!: SignInActorState['email'];

  @signInActorState.field('password')
  private password!: SignInActorState['password'];

  @signInActorState.field('code')
  private code!: SignInActorState['code'];

  @signInActorState.field('busy')
  private busy!: SignInActorState['busy'];

  @signInActorState.field('error')
  private error!: SignInActorState['error'];

  @signInActorState.field('info')
  private info!: SignInActorState['info'];

  private close(): void {
    this.dispatchEvent(
      new CustomEvent('zipper-signin-close', { bubbles: true, composed: true }),
    );
  }

  private finish(res: { token: string; firstName?: string }): void {
    setEmbedToken(this.orgId, res.token);
    this.dispatchEvent(
      new CustomEvent('zipper-signed-in', {
        detail: { firstName: res.firstName },
        bubbles: true,
        composed: true,
      }),
    );
    this.close();
  }

  private async loginPassword(): Promise<void> {
    const email = this.email.trim();
    if (!/\S+@\S+\.\S+/.test(email) || !this.password || this.busy) {
      this.error = 'Enter your email and password.';
      return;
    }
    this.busy = true;
    this.error = '';
    try {
      this.finish(await signInWithPassword(this.orgId, email, this.password));
    } catch {
      this.error = 'Invalid email or password.';
    } finally {
      this.busy = false;
    }
  }

  private async send(): Promise<void> {
    const email = this.email.trim();
    if (!/\S+@\S+\.\S+/.test(email) || this.busy) {
      this.error = 'Enter a valid email.';
      return;
    }
    this.busy = true;
    this.error = '';
    try {
      await requestSignInCode(this.orgId, email);
      this.stage = 'code';
      this.info = `We sent a 6-digit code to ${email}.`;
    } catch {
      this.error = 'Could not send a code. Try again.';
    } finally {
      this.busy = false;
    }
  }

  private async verify(): Promise<void> {
    const code = this.code.trim();
    if (code.length < 6 || this.busy) {
      this.error = 'Enter the 6-digit code.';
      return;
    }
    this.busy = true;
    this.error = '';
    try {
      this.finish(await verifySignInCode(this.orgId, this.email.trim(), code));
    } catch {
      this.error = 'That code is invalid or expired.';
    } finally {
      this.busy = false;
    }
  }

  private renderModeTabs(): TemplateResult {
    const tab = (m: SignInMode, label: string) =>
      html`<button
        type="button"
        class="zsi-tab ${this.mode === m ? 'on' : ''}"
        @click=${() => {
          this.mode = m;
          this.stage = 'email';
          this.error = '';
          this.info = '';
        }}
      >
        ${label}
      </button>`;
    return html`<div class="zsi-tabs">
      ${tab('password', 'Password')}${tab('code', 'Email code')}
    </div>`;
  }

  render(): TemplateResult {
    return html`
      <style>
        .zsi-overlay {
          position: fixed;
          inset: 0;
          background: rgba(20, 24, 28, 0.55);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2147483000;
          padding: 1rem;
        }
        .zsi-modal {
          background: #fff;
          color: #1f2a22;
          border-radius: 12px;
          max-width: 380px;
          width: 100%;
          padding: 1.5rem;
          font-family: system-ui, sans-serif;
          box-shadow: 0 20px 60px -20px rgba(0, 0, 0, 0.5);
        }
        .zsi-modal h3 {
          margin: 0 0 0.25rem;
          font-size: 1.15rem;
        }
        .zsi-tabs {
          display: flex;
          gap: 0.35rem;
          margin: 0.75rem 0 1rem;
        }
        .zsi-tab {
          flex: 1;
          border: 1px solid #e2e2e2;
          background: #fafafa;
          border-radius: 8px;
          padding: 0.45rem 0.5rem;
          font: inherit;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
        }
        .zsi-tab.on {
          background: #fff;
          border-color: #4f9db5;
          color: #2d6f84;
        }
        .zsi-sub {
          font-size: 0.85rem;
          color: #516056;
          margin-bottom: 1rem;
          line-height: 1.4;
        }
        .zsi-input {
          width: 100%;
          box-sizing: border-box;
          border: 1px solid #e2e2e2;
          border-radius: 8px;
          padding: 0.65rem 0.7rem;
          margin-bottom: 0.6rem;
          font: inherit;
          font-size: 16px;
        }
        .zsi-input.code {
          letter-spacing: 0.3em;
          text-align: center;
          font-weight: 700;
        }
        .zsi-btn {
          width: 100%;
          border: 0;
          border-radius: 8px;
          padding: 0.7rem;
          color: #fff;
          font: inherit;
          font-weight: 600;
          cursor: pointer;
        }
        .zsi-btn[disabled] {
          opacity: 0.6;
          cursor: default;
        }
        .zsi-link {
          display: block;
          width: 100%;
          background: none;
          border: 0;
          color: #6b6b6b;
          margin-top: 0.6rem;
          cursor: pointer;
          font: inherit;
        }
        .zsi-err {
          color: #b4541f;
          font-size: 0.85rem;
          margin-bottom: 0.6rem;
        }
      </style>
      <div
        class="zsi-overlay"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this.close();
        }}
      >
        <div class="zsi-modal" role="dialog" aria-modal="true">
          <h3>Sign in</h3>
          ${this.renderModeTabs()}
          ${this.mode === 'password'
            ? html`<div class="zsi-sub">
                  Sign in with the email and password for your account.
                </div>
                ${this.error
                  ? html`<div class="zsi-err">${this.error}</div>`
                  : nothing}
                <input
                  class="zsi-input"
                  type="email"
                  placeholder="you@example.com"
                  .value=${this.email}
                  @input=${(e: Event) => {
                    this.email = (e.target as HTMLInputElement).value;
                    this.error = '';
                  }}
                  @keydown=${(e: KeyboardEvent) =>
                    e.key === 'Enter' && this.loginPassword()}
                />
                <input
                  class="zsi-input"
                  type="password"
                  placeholder="Password"
                  .value=${this.password}
                  @input=${(e: Event) => {
                    this.password = (e.target as HTMLInputElement).value;
                    this.error = '';
                  }}
                  @keydown=${(e: KeyboardEvent) =>
                    e.key === 'Enter' && this.loginPassword()}
                />
                <button
                  class="zsi-btn"
                  style=${`background:${this.accent}`}
                  ?disabled=${this.busy}
                  @click=${() => this.loginPassword()}
                >
                  ${this.busy ? 'Signing in…' : 'Sign in'}
                </button>`
            : this.stage === 'email'
              ? html`<div class="zsi-sub">
                    Enter your email and we'll send you a sign-in code.
                  </div>
                  ${this.error
                    ? html`<div class="zsi-err">${this.error}</div>`
                    : nothing}
                  <input
                    class="zsi-input"
                    type="email"
                    placeholder="you@example.com"
                    .value=${this.email}
                    @input=${(e: Event) => {
                      this.email = (e.target as HTMLInputElement).value;
                      this.error = '';
                    }}
                    @keydown=${(e: KeyboardEvent) =>
                      e.key === 'Enter' && this.send()}
                  />
                  <button
                    class="zsi-btn"
                    style=${`background:${this.accent}`}
                    ?disabled=${this.busy}
                    @click=${() => this.send()}
                  >
                    ${this.busy ? 'Sending…' : 'Send code'}
                  </button>`
              : html`<div class="zsi-sub">${this.info}</div>
                  ${this.error
                    ? html`<div class="zsi-err">${this.error}</div>`
                    : nothing}
                  <input
                    class="zsi-input code"
                    inputmode="numeric"
                    maxlength="6"
                    placeholder="------"
                    .value=${this.code}
                    @input=${(e: Event) => {
                      this.code = (e.target as HTMLInputElement).value.replace(
                        /\D/g,
                        '',
                      );
                      this.error = '';
                    }}
                    @keydown=${(e: KeyboardEvent) =>
                      e.key === 'Enter' && this.verify()}
                  />
                  <button
                    class="zsi-btn"
                    style=${`background:${this.accent}`}
                    ?disabled=${this.busy}
                    @click=${() => this.verify()}
                  >
                    ${this.busy ? 'Verifying…' : 'Verify & sign in'}
                  </button>
                  <button class="zsi-link" @click=${() => this.send()}>
                    Resend code
                  </button>`}
          <button class="zsi-link" @click=${() => this.close()}>Cancel</button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'zipper-signin': ZipperSignin;
  }
}
